// Single source of truth for "benign browser noise" — client errors that carry
// NO actionable signal and must not count against EITHER error sink (Sentry OR
// the owned js_error beacon → events_raw, rule 34). Both sinks share this drop
// list so it can never drift (rules 23/24 "drifting copies").
//
// Verified 2026-06-26 from events_raw (the owned beacon's first 3 days): the
// SecurityError class alone was 166 events / 152 users — the 2nd-biggest error
// class — and 100% benign third-party churn:
//   • Stackless `SecurityError: "The request was denied."` — Google Tag Manager /
//     Google tags invoke a permission-gated API (navigator.share / clipboard /
//     fullscreen / vibrate / a Privacy-Sandbox call) in a denied context on
//     Google-referred mobile sessions. No frame, no URL → unfixable in any page
//     component; it is pure third-party noise (already dropped from Sentry by
//     sentry-pii-scrubber.ts, which shares the needle list below).
//   • Restricted-context SecurityError (localStorage / Web Locks API) from
//     private frames, embedded previews, or enterprise-policy sandboxes.
//   • Cross-origin `Script error.` — the browser masks the real error for CORS,
//     so it carries zero actionable detail (Sentry's default ignoreErrors drops
//     it too).

/** Restricted-context SecurityError messages — the shared needle list. */
export const RESTRICTED_BROWSER_API_NEEDLES = [
  "Failed to read the 'localStorage' property from 'Window'",
  "Failed to execute 'request' on 'LockManager'",
  "Access to the Locks API is denied",
] as const;

// A real stack frame names a call site: `at fn (url:line)`, `fn@url:line`, or a
// bare URL. A SecurityError whose "stack" is only the message line has none.
const STACK_FRAME_RE = /(\bat\s|@|https?:\/\/)/;

/** Whether a stack string contains at least one real call-site frame. */
export function hasStackFrames(stack: string | undefined): boolean {
  return Boolean(stack && STACK_FRAME_RE.test(stack));
}

/**
 * True for a SecurityError that is unactionable browser / third-party noise:
 * a stackless "request was denied" (GTM/Google permission-gated API) OR a
 * restricted-context storage / Web Locks API error. A SecurityError WITH real
 * frames still flows through — that one we can debug.
 */
export function isBenignSecurityError(name: string, message: string, hasFrames: boolean): boolean {
  if (name !== "SecurityError") return false;
  if (!hasFrames) return true;
  return RESTRICTED_BROWSER_API_NEEDLES.some((needle) => message.includes(needle));
}

export interface JsErrorNoiseInput {
  name?: string;
  message?: string;
  stack?: string;
}

/**
 * True when an owned `js_error` beacon should be DROPPED as benign noise, so the
 * events_raw error stream stays a signal of REAL, fixable app failures. Mirrors
 * the Sentry-side scrubber (sentry-pii-scrubber.ts) via the shared needle list.
 */
export function isBenignBrowserNoise(input: JsErrorNoiseInput): boolean {
  const name = input.name ?? "";
  const message = input.message ?? "";
  if (isBenignSecurityError(name, message, hasStackFrames(input.stack))) return true;
  // Cross-origin "Script error." — CORS-masked, zero actionable detail.
  if (message === "Script error." || message === "Script error") return true;
  // Pure-anonymous "Maximum call stack" (no URL frames) = a THIRD-PARTY recursion
  // such as Google's gtag.js Ok<->Mk GTM double-config loop — NOT our code (ours
  // always carries /_next/ chunk URLs in the stack). The real fix is single-owner
  // GA4 config (analytics-ga4.ts); this only keeps the residual third-party
  // recursion out of the owned stream. A real app recursion still flows through.
  if (message === "Maximum call stack size exceeded." && !/https?:\/\//.test(input.stack ?? "")) {
    return true;
  }
  return false;
}
